import type { CoverImage, Site, StoreCategory, Template } from "./api";
import { ALL_TEMPLATES_COVER, findCategory, findSubcategory } from "./taxonomy";

export type { CoverImage };

/**
 * Cover photography for the template gallery, category tiles, and site
 * cards. Template covers come from the catalog API (`coverImage`); category
 * and sub-category photos (lib/taxonomy.ts) are the fallbacks and tile art.
 * Photos are from Unsplash (free to use under the Unsplash License).
 */

const UNSPLASH_HOST = "images.unsplash.com";

/**
 * Sized URL for a cover. Unsplash photos are cropped server-side to the slot
 * they render in; other hosts are used as-is.
 */
export function coverUrl(image: CoverImage, width: number, height: number): string {
  let parsed: URL;
  try {
    parsed = new URL(image.url);
  } catch {
    return image.url;
  }
  if (parsed.host !== UNSPLASH_HOST) {
    return image.url;
  }
  parsed.search = new URLSearchParams({
    auto: "format",
    fit: "crop",
    w: String(width),
    h: String(height),
    q: "75",
  }).toString();
  return parsed.toString();
}

/** 2x source set so covers stay sharp on high-density screens. */
export function coverSrcSet(image: CoverImage, width: number, height: number): string {
  return `${coverUrl(image, width, height)} 1x, ${coverUrl(image, width * 2, height * 2)} 2x`;
}

function unsplash(id: string, alt: string): CoverImage {
  return { url: `https://${UNSPLASH_HOST}/photo-${id}`, alt };
}

export { ALL_TEMPLATES_COVER };

/** Cover for the "Online store" choice in onboarding. */
export const ONLINE_STORE_COVER: CoverImage = unsplash(
  "1441986300917-64674bd600d8",
  "Boutique clothing store interior",
);

/** One cover per store category tile. */
export const STORE_CATEGORY_COVERS: Record<StoreCategory, CoverImage> = {
  fashion: unsplash("1490481651871-ab68de25d43d", "Clothes hanging on a rail"),
  electronics: unsplash("1505740420928-5e560c06d30e", "Headphones on a yellow background"),
  grocery: unsplash("1542838132-92c53300491e", "Fresh produce on grocery shelves"),
  beauty: unsplash("1596462502278-27bfdc403348", "Makeup brushes and products"),
  furniture: unsplash("1586023492125-27b2c045efd7", "Yellow lounge chair in a living room"),
};

/** Cover for a template: the catalog's photo, else its sub-category's or category's. */
export function templateCover(
  template: Pick<Template, "category" | "subcategory" | "kind" | "coverImage">,
): CoverImage {
  return (
    template.coverImage ??
    findSubcategory(template.subcategory)?.cover ??
    findCategory(template.category)?.cover ??
    (template.kind === "store" ? ONLINE_STORE_COVER : ALL_TEMPLATES_COVER)
  );
}

/**
 * Cover for a dashboard site: the photo of the template it was created from.
 * Sites created before templates were recorded (no `templateId`), or whose
 * template left the catalog, use the general workspace photo.
 */
export function siteCover(
  site: Pick<Site, "templateId">,
  templatesById: ReadonlyMap<string, Template>,
): CoverImage {
  const template = site.templateId ? templatesById.get(site.templateId) : undefined;
  return template ? templateCover(template) : ALL_TEMPLATES_COVER;
}
